// Sync Engine Provider
// Reconstructed - February 9, 2026

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useWorkspaceId } from '@/hooks/useWorkspaceId';

interface SyncStatus {
  connected: boolean;
  lastSync: Date | null;
  syncing: boolean;
  error: string | null;
}

interface SyncEngineContextType {
  status: SyncStatus;
  triggerSync: () => Promise<void>;
  pauseSync: () => void;
  resumeSync: () => void;
}

const SyncEngineContext = createContext<SyncEngineContextType | undefined>(undefined);

export function SyncEngineProvider({ children }: { children: ReactNode }) {
  const workspaceId = useWorkspaceId();
  const [status, setStatus] = useState<SyncStatus>({
    connected: false,
    lastSync: null,
    syncing: false,
    error: null,
  });
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!workspaceId || isPaused) return;

    // Initialize sync engine connection
    setStatus((prev) => ({ ...prev, connected: true }));

    // Set up periodic sync (every 30 seconds)
    const syncInterval = setInterval(() => {
      if (!isPaused) {
        triggerSync();
      }
    }, 30000);

    return () => {
      clearInterval(syncInterval);
      setStatus((prev) => ({ ...prev, connected: false }));
    };
  }, [workspaceId, isPaused]);

  const triggerSync = async () => {
    if (isPaused) return;

    setStatus((prev) => ({ ...prev, syncing: true, error: null }));

    try {
      // Perform sync operations
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated sync

      setStatus((prev) => ({
        ...prev,
        syncing: false,
        lastSync: new Date(),
        error: null,
      }));
    } catch (error) {
      setStatus((prev) => ({
        ...prev,
        syncing: false,
        error: error instanceof Error ? error.message : 'Sync failed',
      }));
    }
  };

  const pauseSync = () => {
    setIsPaused(true);
    setStatus((prev) => ({ ...prev, syncing: false }));
  };

  const resumeSync = () => {
    setIsPaused(false);
  };

  return (
    <SyncEngineContext.Provider
      value={{
        status,
        triggerSync,
        pauseSync,
        resumeSync,
      }}
    >
      {children}
    </SyncEngineContext.Provider>
  );
}

function useSyncEngine() {
  const context = useContext(SyncEngineContext);
  if (context === undefined) {
    throw new Error('useSyncEngine must be used within a SyncEngineProvider');
  }
  return context;
}
